import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { CampaignEditor } from '../../components/CampaignEditor';
import { TabNavigation } from '../../components/admin/TabNavigation';
import { SlideOver } from '../../components/admin/SlideOver';
import { BroadcastHistoryList } from '../../components/admin/BroadcastHistoryList';
import { BroadcastDetailPanel } from '../../components/admin/BroadcastDetailPanel';
import { ArrowLeft, Send, Eye, User, Users, AlertCircle, CheckCircle, History } from 'lucide-react';
import { InsertFirstNameModal } from '../../components/InsertFirstNameModal';
import { ConfirmBroadcastModal } from '../../components/ConfirmBroadcastModal';
import { ToastContainer, ToastType } from '../../components/Toast';

interface RecipientCount {
  users: number;
  supportUsers: number;
  total: number;
}

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

type TabType = 'compose' | 'history';

export function AdminBroadcast() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('compose');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [recipientCount, setRecipientCount] = useState<RecipientCount>({ users: 0, supportUsers: 0, total: 0 });
  const [showFirstNameModal, setShowFirstNameModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [firstNameInsertTarget, setFirstNameInsertTarget] = useState<'subject' | 'content'>('subject');
  const [subjectError, setSubjectError] = useState('');
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [selectedBroadcastId, setSelectedBroadcastId] = useState<string | null>(null);
  const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0);

  const defaultContent = `<p>Hoi {{first_name}},</p>

<p>Type hier de body van de notificatie...</p>

<p>Met vriendelijke groet,</p>

<p>
\t<img class="max-w-full h-auto" src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/693a6e462d47394a7093d081_hallo_dibbi.png" alt="dibbi logo" width="150px" height="auto">
</p>

<p>PS: je kunt hier nog een ps typen...</p>`;

  useEffect(() => {
    if (user) {
      loadTemplate();
      loadRecipientCount();
      setLoading(false);
    }
  }, [user]);

  const showToast = (type: ToastType, title: string, message?: string, duration?: number) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, type, title, message, duration }]);
  };

  const closeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const loadTemplate = async () => {
    try {
      const { data: template, error } = await supabase
        .from('email_templates')
        .select('subject, html_body')
        .eq('template_type', 'admin_broadcast')
        .maybeSingle();

      if (error) throw error;

      if (template) {
        setSubject(template.subject || '');
      }

      setContent(defaultContent);
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  const loadRecipientCount = async () => {
    try {
      const [usersRes, supportRes] = await Promise.all([
        supabase.from('users').select('id', { count: 'exact', head: true }).not('email', 'is', null),
        supabase.from('support_users').select('id', { count: 'exact', head: true }).eq('is_active', true).not('email', 'is', null),
      ]);

      const userCount = usersRes.count || 0;
      const supportCount = supportRes.count || 0;

      setRecipientCount({
        users: userCount,
        supportUsers: supportCount,
        total: userCount + supportCount,
      });
    } catch (error) {
      console.error('Error loading recipient count:', error);
    }
  };

  const handleSendTest = async () => {
    if (!subject || !content) return;

    try {
      setSendingTest(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Geen sessie gevonden');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-broadcast-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            subject,
            content,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fout bij versturen test email');
      }

      showToast('success', 'Test email verzonden!', 'De test email is verzonden naar hallo@dibbi.nl (Mischa)');
    } catch (error) {
      console.error('Error sending test email:', error);
      showToast(
        'error',
        'Fout bij versturen test email',
        error instanceof Error ? error.message : 'Er ging iets mis bij het versturen van de test email'
      );
    } finally {
      setSendingTest(false);
    }
  };

  const handleInsertFirstName = (fallback: string) => {
    const placeholder = `{{first_name|${fallback}}}`;

    if (firstNameInsertTarget === 'subject') {
      setSubject((prev) => prev + placeholder);
    } else {
      const editor = document.querySelector('.tiptap');
      if (editor) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(placeholder));
        }
      }
      setContent((prev) => prev + placeholder);
    }
  };

  const validateSubject = () => {
    if (subjectTouched && !subject.trim()) {
      setSubjectError('Subject is verplicht');
      return false;
    } else {
      setSubjectError('');
      return true;
    }
  };

  const handleSubjectChange = (value: string) => {
    setSubject(value);
    if (subjectTouched || value.trim()) {
      setSubjectTouched(true);
    }
  };

  const handleSubjectBlur = () => {
    setSubjectTouched(true);
    validateSubject();
  };

  const handleConfirmBroadcast = () => {
    if (!user || !subject.trim() || !content.trim()) {
      showToast('warning', 'Vul alle velden in', 'Zowel een onderwerp als inhoud zijn verplicht');
      return;
    }

    if (!validateSubject()) {
      return;
    }

    setShowConfirmModal(true);
  };

  const handleSendBroadcast = async () => {
    setShowConfirmModal(false);

    if (!user) {
      showToast('error', 'Niet ingelogd', 'Kan broadcast niet versturen zonder ingelogde gebruiker.');
      return;
    }

    try {
      setSending(true);

      const { data: campaign, error: campaignError } = await supabase
        .from('email_campaigns')
        .insert({
          user_id: user.id,
          subject,
          content,
          campaign_type: 'admin_broadcast',
          status: 'draft',
          total_contacts: recipientCount.total,
        })
        .select()
        .single();

      if (campaignError) throw campaignError;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-admin-broadcast`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            campaign_id: campaign.id,
            admin_user_id: user.id,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send broadcast');
      }

      const result = await response.json();

      setSendSuccess(true);
      setHistoryRefreshTrigger((prev) => prev + 1);
      showToast(
        'success',
        'Broadcast verzonden!',
        `De broadcast is succesvol verzonden naar ${result.sent} gebruikers`,
        7000
      );

      setSubject('');
      setContent('');
      setSubjectTouched(false);

      setTimeout(() => {
        setSendSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error sending broadcast:', error);
      showToast(
        'error',
        'Fout bij versturen broadcast',
        error instanceof Error ? error.message : 'Er ging iets mis bij het versturen van de broadcast'
      );
    } finally {
      setSending(false);
    }
  };

  const handleReuseAsTemplate = (templateSubject: string, templateContent: string) => {
    setSubject(templateSubject);
    setContent(templateContent);
    setSelectedBroadcastId(null);
    setActiveTab('compose');
    showToast('success', 'Template geladen', 'Het onderwerp en de inhoud zijn overgenomen. Pas aan en verstuur.');
  };

  const tabs = [
    { id: 'compose', label: 'Nieuwe Broadcast', icon: Send },
    { id: 'history', label: 'Verzonden', icon: History },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Broadcast</h1>
              <p className="text-sm text-gray-600 mt-1">Verstuur een bericht naar alle gebruikers</p>
            </div>
          </div>
        </div>

        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as TabType)}
        />

        {activeTab === 'compose' && (
          <>
            {sendSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900">Broadcast verzonden!</h3>
                  <p className="text-sm text-green-700 mt-1">Je broadcast is succesvol verstuurd naar alle gebruikers.</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Ontvangers</h2>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Reguliere gebruikers:</span>
                    <span className="font-medium text-gray-900">{recipientCount.users}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Support users:</span>
                    <span className="font-medium text-gray-900">{recipientCount.supportUsers}</span>
                  </div>
                  <div className="border-t border-blue-300 my-2"></div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-900">Totaal ontvangers:</span>
                    <span className="font-bold text-blue-600 text-lg">{recipientCount.total}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-2 text-sm text-gray-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  Dit bericht wordt verstuurd naar ALLE gebruikers in het systeem.
                  Zorg ervoor dat je bericht relevant is voor iedereen.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Onderwerp *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                    onBlur={handleSubjectBlur}
                    placeholder="Bijv: Belangrijke update over Dibbi"
                    className={`flex-1 px-4 py-3 border ${
                      subjectError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    } rounded-lg focus:ring-2 focus:border-transparent`}
                  />
                  <button
                    onClick={() => {
                      setFirstNameInsertTarget('subject');
                      setShowFirstNameModal(true);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    title="Voeg voornaam placeholder toe"
                  >
                    <User className="w-4 h-4" />
                  </button>
                </div>
                {subjectError && (
                  <p className="mt-1 text-sm text-red-600">{subjectError}</p>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Inhoud *
                </label>
                <div className="relative">
                  <CampaignEditor content={content} onChange={setContent} toolbarStickyClass="top-0" />
                  <button
                    onClick={() => {
                      setFirstNameInsertTarget('content');
                      setShowFirstNameModal(true);
                    }}
                    className="absolute top-2 right-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm shadow-sm z-10"
                    title="Voeg voornaam placeholder toe"
                  >
                    <User className="w-4 h-4" />
                    Voornaam
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleConfirmBroadcast}
                  disabled={!subject.trim() || !content.trim() || sending || sendingTest}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-semibold"
                >
                  {sending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Bezig met versturen...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Verstuur Broadcast
                    </>
                  )}
                </button>

                <button
                  onClick={handleSendTest}
                  disabled={!subject.trim() || !content.trim() || sending || sendingTest}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {sendingTest ? (
                    <>
                      <div className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Versturen...</span>
                    </>
                  ) : (
                    <>
                      <Eye className="w-5 h-5" />
                      Test Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'history' && (
          <BroadcastHistoryList
            onSelectBroadcast={(id) => setSelectedBroadcastId(id)}
            refreshTrigger={historyRefreshTrigger}
          />
        )}
      </div>

      <SlideOver
        isOpen={!!selectedBroadcastId}
        onClose={() => setSelectedBroadcastId(null)}
        title="Broadcast Details"
      >
        {selectedBroadcastId && (
          <BroadcastDetailPanel
            broadcastId={selectedBroadcastId}
            onReuseAsTemplate={handleReuseAsTemplate}
          />
        )}
      </SlideOver>

      {showFirstNameModal && (
        <InsertFirstNameModal
          isOpen={showFirstNameModal}
          onInsert={handleInsertFirstName}
          onClose={() => setShowFirstNameModal(false)}
        />
      )}

      {showConfirmModal && (
        <ConfirmBroadcastModal
          isOpen={showConfirmModal}
          recipientCount={recipientCount}
          onConfirm={handleSendBroadcast}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      <ToastContainer toasts={toasts} onClose={closeToast} />
    </AdminLayout>
  );
}
